import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, HelpCircle, Loader2, RefreshCw } from "lucide-react";

import { fetchReconciliation } from "../lib/api";

/**
 * Each replicated report's PortalIQ figure beside the HubSpot figure it copies.
 *
 * This is not polish. Once the same filter logic lives in two systems they
 * drift, and drift is silent: HubSpot report 369308847 is currently dropping
 * three of its ten filter rules and rendering seven, and nobody noticed. A
 * visible diff is what turns that into something someone sees.
 */

const STATUS_STYLES = {
  match: { icon: CheckCircle2, className: "text-emerald-600", label: "Agrees" },
  diff: { icon: AlertTriangle, className: "text-amber-600", label: "Differs" },
  no_baseline: { icon: HelpCircle, className: "text-[#7d8b96]", label: "No HubSpot figure" },
  not_comparable: { icon: HelpCircle, className: "text-[#7d8b96]", label: "Not comparable" },
  error: { icon: AlertTriangle, className: "text-red-600", label: "Failed to run" },
  unknown: { icon: HelpCircle, className: "text-[#7d8b96]", label: "Unknown" },
};

const number = (value) =>
  value === null || value === undefined ? "—" : new Intl.NumberFormat("en-GB").format(value);

export default function ReconciliationPanel({ portalId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (force = false) => {
      if (!portalId) return;
      setLoading(true);
      setError(null);
      try {
        setData(await fetchReconciliation(portalId, force));
      } catch (err) {
        setError(err?.response?.data?.detail || "Could not run the reconciliation.");
      } finally {
        setLoading(false);
      }
    },
    [portalId],
  );

  useEffect(() => {
    load();
  }, [load]);

  if (!portalId) {
    return <p className="text-sm text-[#4a5b68]">Connect a HubSpot portal to reconcile reports.</p>;
  }

  return (
    <div className="space-y-4" data-testid="reconciliation-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {data && (
            <>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                {data.matched} agree
              </span>
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                {data.differing} differ
              </span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {data.unverified} unverified
              </span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full border border-[#cccccc] bg-white px-3 py-1.5 text-sm font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996] disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Re-run against live data
        </button>
      </div>

      {error && (
        <p className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {error}
        </p>
      )}

      <div className="overflow-x-auto rounded-md border border-[#cccccc] bg-white">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-[#cccccc] bg-[#f5f5f5]">
              {["Report", "HubSpot", "PortalIQ", "Difference", "Status"].map((heading, index) => (
                <th
                  key={heading}
                  className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4a5b68] ${
                    index === 0 ? "text-left" : "text-right"
                  } ${index === 4 ? "text-left" : ""}`}
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((row) => {
              const status = STATUS_STYLES[row.status] || STATUS_STYLES.unknown;
              const StatusIcon = status.icon;
              return (
                <tr key={row.key} className="border-b border-[#efefef] last:border-0">
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-[#323c44]">{row.title}</span>
                    {row.hubspot_report_id && (
                      <a
                        href={row.hubspot_report_url || `https://app.hubspot.com/reports-dashboard/all/report/${row.hubspot_report_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-2 inline-flex items-center gap-1 text-[11px] text-[#7d8b96] hover:text-[#119996]"
                      >
                        {row.hubspot_report_id} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {row.error && <p className="mt-0.5 text-[11px] text-red-600">{row.error}</p>}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[#323c44]">{number(row.hubspot_value)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-[#323c44]">{number(row.portaliq_value)}</td>
                  <td
                    className={`px-4 py-2.5 text-right tabular-nums font-semibold ${
                      row.difference ? "text-amber-700" : "text-[#7d8b96]"
                    }`}
                  >
                    {row.difference === null || row.difference === undefined
                      ? "—"
                      : `${row.difference > 0 ? "+" : ""}${number(row.difference)}`}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${status.className}`}>
                      <StatusIcon className="h-4 w-4" />
                      {status.label}
                    </span>
                  </td>
                </tr>
              );
            })}
            {!loading && !(data?.rows || []).length && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-[#4a5b68]">
                  No report definitions to reconcile yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data?.generated_at && (
        <p className="text-[11px] text-[#7d8b96]">
          Generated {new Date(data.generated_at).toLocaleString("en-GB")}. HubSpot figures are the ones
          recorded on each definition — update one when its HubSpot report changes.
        </p>
      )}
    </div>
  );
}
